import { ReactNode, useMemo, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfigProvider } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/ar';
import 'dayjs/locale/fa';
import enUS from 'antd/locale/en_US';
import { getTheme } from './theme';
import { useRTL, LOCALES } from 'configs/constants';
import useDocumentAttribUpdater from 'utils/use-document-attrib-updater';

interface AntdConfigProviderProps {
  children: ReactNode;
}

const AntdConfigProvider = memo(({ children }: AntdConfigProviderProps) => {
  const { i18n } = useTranslation();
  const isRTL = useRTL();
  const language = i18n.language;
  const direction = i18n.dir(language);

  // Memoize theme object since it only depends on isRTL
  const theme = useMemo(() => getTheme(isRTL), [isRTL]);

  // Memoize locale object based on current language
  const currentLocale = useMemo(() => {
    return LOCALES[language as keyof typeof LOCALES] || enUS;
  }, [language]);

  useDocumentAttribUpdater({ lang: language, dir: direction });

  // Update dayjs locale immediately when language changes
  dayjs.locale(language);

  return (
    <ConfigProvider key={language} locale={currentLocale} direction={direction} theme={theme}>
      {children}
    </ConfigProvider>
  );
});

AntdConfigProvider.displayName = 'AntdConfigProvider';

export default AntdConfigProvider;
